import { create } from 'zustand'

import { api, errorMessage } from '@/lib/api'
import { type AsyncState, failed, idle, isStale, loaded, loading } from '@/lib/async-state'
import type { DashboardSummary, ServiceHealth } from '@/types/api'

interface DashboardState {
  summary: AsyncState<DashboardSummary | null>
  health: AsyncState<ServiceHealth | null>
  windowDays: number

  load: (options?: { force?: boolean }) => Promise<void>
  loadHealth: () => Promise<void>
  setWindowDays: (days: number) => Promise<void>
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  summary: idle<DashboardSummary | null>(null),
  health: idle<ServiceHealth | null>(null),
  windowDays: 30,

  load: async ({ force } = {}) => {
    const state = get()
    // Polling the dashboard repeatedly is normal; skip the round trip when the
    // data is still fresh unless the caller explicitly wants it refetched.
    if (!force && state.summary.loading) return
    if (!force && !isStale(state.summary, 20_000)) return

    set({ summary: loading(state.summary) })
    try {
      const data = await api.get<DashboardSummary>('/dashboard', {
        query: { days: state.windowDays },
      })
      set({ summary: loaded<DashboardSummary | null>(data) })
    } catch (error) {
      set({ summary: failed(get().summary, errorMessage(error)) })
    }
  },

  loadHealth: async () => {
    set({ health: loading(get().health) })
    try {
      const data = await api.get<ServiceHealth>('/dashboard/health')
      set({ health: loaded<ServiceHealth | null>(data) })
    } catch (error) {
      set({ health: failed(get().health, errorMessage(error)) })
    }
  },

  setWindowDays: async (windowDays) => {
    set({ windowDays })
    await get().load({ force: true })
  },
}))
